import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAccountsStore } from "./store";
import { backfillBrokerLogos, initializeAccountsStore } from "./hooks";
import { decodeConnectToken, connectPayloadToAccount } from "./connectLink";
import {
  getVybeToken,
  takeFragmentConnectToken,
  whenVybeAuthSettled,
} from "../vybe-auth/store";
import { fetchGateways } from "../gateways/hooks";

/** Decode a connect token and add/select the account — shared by the query-param and fragment paths. */
function handleConnectToken(token: string): void {
  const payload = decodeConnectToken(token);
  if (!payload) {
    toast.error("That account link is invalid or has expired.");
    return;
  }

  // Ensure the store is hydrated from localStorage before we mutate it (effect ordering is not
  // guaranteed relative to the parent's). initializeAccountsStore() is an idempotent load.
  initializeAccountsStore();

  const store = useAccountsStore.getState();
  const login = String(payload.login);

  // Match on login + host normally. For Vybe-gateway accounts, match on LOGIN ALONE: the same trading account
  // can be fronted by a different gateway host over time (proposal 70 moved these links from the generic
  // ViiSync host to the branded Vybe one), and a host-sensitive match would leave the trader with two rows for
  // one account — the stale one first in the list, still pointing at the old gateway.
  const existing =
    store.accounts.find(
      (a) => a.credentials.login === login && (a.host ?? "") === payload.host
    ) ??
    store.accounts.find(
      (a) => a.credentials.login === login && a.providerKind === "Vybe"
    );

  if (existing) {
    // Already saved locally → re-point it at the gateway this link names, then select it; it reconnects with
    // the password already stored on this device (works even when the link carries no password, e.g. the CRM
    // "Open in terminal" button). Credentials are never touched here — only the gateway/branding fields.
    store.updateAccount({
      ...existing,
      companyName: payload.companyName,
      serverName: payload.serverName,
      host: payload.host,
      port: payload.port,
      hubUrl: payload.hubUrl ?? existing.hubUrl,
      gatewaySlug: payload.gatewaySlug ?? existing.gatewaySlug,
      brokerLogoUrl: payload.logoUrl ?? existing.brokerLogoUrl,
    });
    store.setActiveAccountId(existing.id);
    toast.success(`Account ${payload.login} selected from your link — connecting…`);
  } else if (payload.password) {
    // New account WITH a password (credentials email / onboarding autopilot) → add + set active → auto-connect.
    store.addAccount(connectPayloadToAccount(payload));
    toast.success(`Account ${payload.login} added — connecting…`);
  } else {
    // New account WITHOUT a password (the CRM "Open in terminal" button — the CRM can't read an existing
    // account's password). Persist + select it, but the connection hook skips the doomed blank-password
    // connect (no false "Connection failed" modal). The user types the password once in the account
    // manager (Edit Account → Password) to connect.
    store.addAccount(connectPayloadToAccount(payload));
    toast.info(`Account ${payload.login} added — open Accounts and enter your password to connect.`);
  }

  // Repair path only. Tokens minted after proposal 70 carry `logoUrl`, so the tile is already correct and the
  // backfill skips the account (it only touches rows with no `brokerLogoUrl`). Older links — and any gateway
  // whose catalog entry has no logo — still resolve their company + mark from the catalog here.
  void backfillBrokerLogos();
}

/**
 * Load-time handler for the "Open in terminal" email deep-link (`?connect=<token>`).
 *
 * On boot it reads the `connect` param, strips it from the URL immediately (so the credential-bearing token
 * isn't re-triggered on re-render or leaked via history / referrer), decodes + validates the payload, then:
 *  - if an account with the same login+host is already saved → selects it (idempotent — "it stays added");
 *  - otherwise → adds it via the account store (which persists it and sets it active).
 *
 * Setting the active account is enough to connect: `useAccountConnection` already reacts to `activeAccountId`
 * and fires the existing `ConnectionCredentials` connect path — no new transport here.
 */
export function useConnectDeepLink(): void {
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    if (typeof window === "undefined") return;

    // (1) Query-param path — the "Open in terminal" email link (`?connect=<token>`).
    const params = new URLSearchParams(window.location.search);
    const queryToken = params.get("connect");
    if (queryToken) {
      handled.current = true;

      // Strip the token from the URL up front — it carries credentials and must not linger or re-fire.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("connect");
        window.history.replaceState({}, "", url.toString());
      } catch {
        /* non-fatal */
      }

      handleConnectToken(queryToken);
      return;
    }

    // (2) Fragment path — onboarding autopilot (proposal 52): `#vt-handoff=<code>&connect=<token>`.
    // The auth store parsed + scrubbed the fragment; we wait for the handoff redeem to settle first
    // (session before account), then run the exact same add-with-password + auto-connect flow.
    const fragmentToken = takeFragmentConnectToken();
    if (fragmentToken) {
      handled.current = true;
      void whenVybeAuthSettled().then(() => handleConnectToken(fragmentToken));
      return;
    }

    // (3) Zero-account recovery (terminal-first signups, single-connection auth). A user who
    // registered INSIDE the terminal (Google popup / email card) has a session but no account row —
    // their fragment only carried `vt-handoff`. Add a session-backed Vybe account pointing at the
    // Vybe gateway: it carries NO password, and the connection sends the CRM session token instead
    // (X-Vybe-Token) — the GATEWAY resolves and, on the very first connect, provisions the starter
    // account server-side. No credential ever reaches this browser.
    handled.current = true;
    void whenVybeAuthSettled().then(async () => {
      const token = getVybeToken();
      if (!token) return;

      initializeAccountsStore();
      if (useAccountsStore.getState().accounts.length > 0) return;

      try {
        const gateways = await fetchGateways();
        const vybe =
          gateways.find((g) => g.slug === "sig-vybe") ??
          gateways.find((g) => g.slug.includes("vybe"));
        if (!vybe?.hubUrl) return;
        // Re-check: the fragment path or a second tab may have added an account meanwhile.
        if (useAccountsStore.getState().accounts.length > 0) return;

        const server = vybe.servers?.[0];
        const store = useAccountsStore.getState();
        store.addAccount({
          companyName: vybe.name || "Vybe Trader",
          serverName: server?.name ?? "Vybe Trader Live",
          providerKind: "Vybe",
          credentials: { login: "auto", password: "" },
          createdAt: Date.now(),
          lastConnectedAt: 0,
          host: server?.host ?? "",
          port: server?.port ?? 0,
          hubUrl: vybe.hubUrl,
          gatewaySlug: vybe.slug,
        });
        toast.success("Setting up your trading account — connecting…");
      } catch {
        // CRM unreachable — the manual account dialog remains the fallback.
      }
    });
  }, []);
}
